import React, { useCallback, useEffect, useRef, useState } from 'react';
import { tenantApi, type ShiftReminderPreferences, type ShiftReminderPreferencesUpdate } from '../services/tenantApi';
import './ShiftReminderPreferencesCard.css';

type OptInKey = keyof ShiftReminderPreferencesUpdate;

const OPT_IN_ROWS: Array<{ key: OptInKey; label: string; description: string }> = [
  { key: 'remind7d', label: '7 days before', description: 'A heads-up a week before your on-call shift starts' },
  { key: 'remind24h', label: '24 hours before', description: 'A reminder the day before your shift starts' },
  { key: 'remind8h', label: 'Start of day (8 hours before)', description: 'A reminder on the morning of your shift' },
];

const MANDATORY_ROWS = [
  { key: 'oneHour', label: '1 hour before', description: 'Push and email; SMS too if no logged-in device is detected' },
  { key: 'start', label: 'Shift start (alert tone)', description: 'Push with the shift-start tone and an in-app alert; SMS if no logged-in device' },
];

const MANDATORY_TOOLTIP = 'Required for every on-call operator so nobody misses the start of a shift. This reminder cannot be turned off.';

interface ShiftReminderPreferencesCardProps {
  /** Identity of the signed-in user. A change resets state and discards in-flight responses. */
  userId: string | null | undefined;
}

/** BL-043 UI-061: opt-in 7d/24h/8h reminders; 1h and shift-start are shown locked as mandatory. */
export const ShiftReminderPreferencesCard: React.FC<ShiftReminderPreferencesCardProps> = ({ userId }) => {
  const [prefs, setPrefs] = useState<ShiftReminderPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pending, setPending] = useState<Partial<Record<OptInKey, boolean>>>({});
  const identityRef = useRef(userId);

  const load = useCallback(async (identity: string | null | undefined) => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await tenantApi.getShiftReminderPreferences();
      if (identityRef.current !== identity) return;
      setPrefs(result);
    } catch {
      if (identityRef.current !== identity) return;
      setLoadError('Could not load shift reminder preferences.');
    } finally {
      if (identityRef.current === identity) setLoading(false);
    }
  }, []);

  useEffect(() => {
    identityRef.current = userId;
    setPrefs(null);
    setPending({});
    setSaveError(null);
    if (!userId) {
      setLoading(false);
      return;
    }
    void load(userId);
  }, [userId, load]);

  const toggle = async (key: OptInKey, value: boolean) => {
    if (!prefs || pending[key]) return;
    const identity = identityRef.current;
    const previous = prefs[key];
    setSaveError(null);
    setPrefs(current => (current ? { ...current, [key]: value } : current));
    setPending(current => ({ ...current, [key]: true }));
    try {
      const saved = await tenantApi.updateShiftReminderPreferences({ [key]: value });
      if (identityRef.current !== identity) return;
      // Only adopt the saved value for this row so other in-flight toggles are not overwritten.
      setPrefs(current => (current ? { ...current, [key]: saved?.[key] ?? value } : current));
    } catch {
      if (identityRef.current !== identity) return;
      setPrefs(current => (current ? { ...current, [key]: previous } : current));
      setSaveError('Could not save your reminder preference. Please try again.');
    } finally {
      if (identityRef.current === identity) setPending(current => ({ ...current, [key]: false }));
    }
  };

  return (
    <section className="shift-reminder-card" aria-labelledby="shift-reminder-heading">
      <h3 id="shift-reminder-heading">Shift reminders</h3>
      <p className="section-description">
        Reminders before your on-call shifts. Times are shown in your profile time zone.
      </p>

      {loading && <p className="shift-reminder-status" role="status">Loading reminder preferences…</p>}
      {loadError && (
        <div className="shift-reminder-error" role="alert">
          {loadError}{' '}
          <button type="button" className="shift-reminder-retry" onClick={() => void load(identityRef.current)}>Retry</button>
        </div>
      )}
      {prefs && prefs.remindersEnabled === false && (
        <p className="shift-reminder-status" role="note">Shift reminders are currently paused by your administrator.</p>
      )}
      {saveError && <div className="shift-reminder-error" role="alert">{saveError}</div>}

      {prefs && (
        <div className="toggle-group shift-reminder-rows">
          {OPT_IN_ROWS.map(row => (
            <label key={row.key} className="toggle-item shift-reminder-row">
              <span className="toggle-info">
                <strong>{row.label}</strong>
                <small>{row.description}</small>
              </span>
              <input
                type="checkbox"
                aria-label={row.label}
                checked={prefs[row.key]}
                disabled={!!pending[row.key]}
                onChange={e => void toggle(row.key, e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          ))}
          {MANDATORY_ROWS.map(row => (
            <div key={row.key} className="toggle-item shift-reminder-row shift-reminder-row--locked" title={MANDATORY_TOOLTIP} data-testid={`shift-reminder-${row.key}`}>
              <span className="toggle-info">
                <strong>
                  {row.label}
                  <span className="shift-reminder-chip" aria-label="Mandatory">Mandatory</span>
                </strong>
                <small>{row.description}</small>
              </span>
              <label className="shift-reminder-locked-toggle" title={MANDATORY_TOOLTIP}>
                <input type="checkbox" aria-label={`${row.label} (mandatory)`} checked readOnly disabled aria-describedby="shift-reminder-mandatory-note" />
                <span className="toggle-slider"></span>
              </label>
            </div>
          ))}
          <p id="shift-reminder-mandatory-note" className="help-text">{MANDATORY_TOOLTIP}</p>
        </div>
      )}
    </section>
  );
};

export default ShiftReminderPreferencesCard;
